import type { EditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import { countEditorialChecklistProgress } from "@/lib/editorial-checklist-progress";
import type {
  ContentStatus,
  CreateContentInitialStatus,
} from "@/core/services/types/article.types";
import {
  appendLibrarySortParams,
  getNextLibrarySort,
  parseLibrarySortDirection,
  type LibrarySortDirection,
} from "@/core/services/types/library-sort.types";
import {
  DEFAULT_LIBRARY_PAGE_SIZE,
  appendLibraryPageSizeParam,
  parseLibraryPageSize,
  type LibraryPageSize,
} from "@/core/services/types/library-pagination.types";
import type { Enums, Tables } from "@/types/database.types";

export type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export const DAYS_OF_WEEK: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export type PlaceOpeningHourEntry = {
  day: DayOfWeek;
  isOpen: boolean;
  open: string;
  close: string;
};

export type PlaceOpeningHours = PlaceOpeningHourEntry[];

export const PLACE_TYPE_VALUES = [
  "ARTIST_RUN_SPACE",
  "WORKSHOP",
  "PUBLIC_BUILDING",
  "LIBRARY",
  "COMPANY",
  "PUBLIC_SPACE",
  "FOUNDATION",
  "GALLERY",
  "TEMPORARY_EXHIBITION_VENUE",
  "CREATION_SPACE",
  "SHOP",
  "ARTIST_RESIDENCE",
  "CULTURAL_CENTER",
  "MANUFACTORY",
  "MEDIA_LIBRARY",
  "MUSEUM",
  "PARK_GARDEN",
  "PRIVATE_PROPERTY",
  "RESTAURANT_CAFE",
  "THIRD_PLACE",
] as const satisfies readonly Enums<"place_type_enum">[];

export type PlaceTypeValue = (typeof PLACE_TYPE_VALUES)[number];

export type PlaceEditStatusValue = ContentStatus;

export type PlaceStatusActionVariant = "primary" | "secondary" | "danger";

export type PlaceStatusActionDisabledHintKey = "publishChecklistIncomplete";

export type PlaceStatusAction = {
  target?: PlaceEditStatusValue;
  labelKey:
    | "submitReview"
    | "publish"
    | "revertDraft"
    | "promoteToDraft"
    | "archive"
    | "delete";
  variant: PlaceStatusActionVariant;
  disabled?: boolean;
  disabledHintKey?: PlaceStatusActionDisabledHintKey;
};

export function isPlacePublishStatus(status: PlaceEditStatusValue): boolean {
  return status === "PUBLISHED";
}

const PLACE_EDITORIAL_DELETE_ACTION: PlaceStatusAction = {
  labelKey: "delete",
  variant: "danger",
};

export function getPlaceStatusActions(
  currentStatus: PlaceEditStatusValue,
  options?: {
    canPublish?: boolean;
    canDelete?: boolean;
    publishDisabled?: boolean;
  },
): PlaceStatusAction[] {
  let actions = getPlaceStatusActionsForStatus(currentStatus);

  if (options?.canDelete === false) {
    actions = actions.filter((action) => action.labelKey !== "delete");
  }

  if (options?.canPublish === false) {
    return actions.filter(
      (action) => !action.target || !isPlacePublishStatus(action.target),
    );
  }

  if (options?.publishDisabled) {
    actions = actions.map((action) =>
      action.target && isPlacePublishStatus(action.target)
        ? {
            ...action,
            disabled: true,
            disabledHintKey: "publishChecklistIncomplete",
          }
        : action,
    );
  }

  return actions;
}

function getPlaceStatusActionsForStatus(
  currentStatus: PlaceEditStatusValue,
): PlaceStatusAction[] {
  switch (currentStatus) {
    case "SIGNAL":
      return [
        { target: "DRAFT", labelKey: "promoteToDraft", variant: "primary" },
        PLACE_EDITORIAL_DELETE_ACTION,
      ];
    case "DRAFT":
      return [
        {
          target: "PENDING_REVIEW",
          labelKey: "submitReview",
          variant: "secondary",
        },
        { target: "PUBLISHED", labelKey: "publish", variant: "primary" },
        PLACE_EDITORIAL_DELETE_ACTION,
      ];
    case "PENDING_REVIEW":
      return [
        { target: "DRAFT", labelKey: "revertDraft", variant: "secondary" },
        { target: "PUBLISHED", labelKey: "publish", variant: "primary" },
        PLACE_EDITORIAL_DELETE_ACTION,
      ];
    case "SCHEDULED":
      return [];
    case "PUBLISHED":
      return [{ target: "ARCHIVED", labelKey: "archive", variant: "danger" }];
    case "ARCHIVED":
      return [
        { target: "DRAFT", labelKey: "revertDraft", variant: "secondary" },
      ];
  }
}

export function buildGoogleMapsPlaceUrl(
  googlePlaceId: string | null,
  query?: string | null,
): string | null {
  if (!googlePlaceId?.trim()) return null;
  const params = new URLSearchParams({
    api: "1",
    query: query?.trim() || " ",
    query_place_id: googlePlaceId.trim(),
  });
  return `https://www.google.com/maps/search/?${params.toString()}`;
}

function hasPlaceCoordinates(
  latitude: number,
  longitude: number,
): boolean {
  return (
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    !(latitude === 0 && longitude === 0)
  );
}

export function getPlacePublishFieldErrors(
  place: Pick<PlaceEditor, "zip_code" | "latitude" | "longitude">,
  labels: {
    zipCodeRequired: string;
    coordinatesRequired: string;
  },
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  if (!place.zip_code?.trim()) {
    fieldErrors.zip_code = labels.zipCodeRequired;
  }

  if (!hasPlaceCoordinates(place.latitude, place.longitude)) {
    fieldErrors.google_place_id = labels.coordinatesRequired;
  }

  return fieldErrors;
}

export function getPlaceContentPublishFieldErrors(
  place: Pick<PlaceEditor, "name" | "zip_code" | "latitude" | "longitude">,
  messages: {
    nameRequired: string;
    zipCodeRequired: string;
    coordinatesRequired: string;
  },
): Record<string, string> {
  const fieldErrors = getPlacePublishFieldErrors(place, {
    zipCodeRequired: messages.zipCodeRequired,
    coordinatesRequired: messages.coordinatesRequired,
  });

  if (!place.name?.trim()) {
    fieldErrors.name = messages.nameRequired;
  }

  return fieldErrors;
}

export const PLACE_LIBRARY_TABS = [
  "ALL",
  "DRAFT",
  "PENDING_REVIEW",
  "PUBLISHED",
  "ARCHIVED",
] as const;

export type PlaceLibraryTab = (typeof PLACE_LIBRARY_TABS)[number];

export type PlaceAdminListItem = Pick<
  Tables<"places">,
  | "id"
  | "name"
  | "city"
  | "zip_code"
  | "place_type"
  | "status"
  | "created_at"
  | "updated_at"
  | "cover_image_url"
  | "google_place_id"
  | "address"
>;

export type PlaceListPage = {
  items: PlaceAdminListItem[];
  total: number;
  page: number;
  pageSize: LibraryPageSize;
};

export type PlaceLibraryTabCounts = Record<PlaceLibraryTab, number>;

export type PlaceLibraryKpis = {
  total: number;
  publishedThisMonth: number;
  pendingReview: number;
};

export const PLACE_LIBRARY_PAGE_SIZE = DEFAULT_LIBRARY_PAGE_SIZE;

export { parseLibraryPageSize as parsePlacePageSize };
export const PLACE_SEARCH_MAX_LENGTH = 100;

export function getPlaceSharePath(id: string): string {
  return `/places/redaction/${id}`;
}

export function parsePlaceLibraryTab(value: string | undefined): PlaceLibraryTab {
  if (value && PLACE_LIBRARY_TABS.includes(value as PlaceLibraryTab)) {
    return value as PlaceLibraryTab;
  }
  return "ALL";
}

export function parsePlacePage(value: string | undefined): number {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 1) return 1;
  return parsed;
}

export function parsePlaceSearchQuery(value: string | undefined): string {
  if (!value) return "";
  return value.trim().slice(0, PLACE_SEARCH_MAX_LENGTH);
}

export const PLACE_LIBRARY_SORT_COLUMNS = [
  "name",
  "place_type",
  "city",
  "zip_code",
  "created_at",
  "updated_at",
  "status",
] as const;

export type PlaceLibrarySortColumn = (typeof PLACE_LIBRARY_SORT_COLUMNS)[number];

export type PlaceLibrarySort = {
  column: PlaceLibrarySortColumn;
  direction: LibrarySortDirection;
};

export type PlaceLibrarySortState = PlaceLibrarySort | null;

export function parsePlaceLibrarySort(
  column: string | undefined,
  direction: string | undefined,
): PlaceLibrarySortState {
  if (
    !column ||
    !PLACE_LIBRARY_SORT_COLUMNS.includes(column as PlaceLibrarySortColumn)
  ) {
    return null;
  }

  return {
    column: column as PlaceLibrarySortColumn,
    direction: parseLibrarySortDirection(direction),
  };
}

export function buildPlaceLibraryHref({
  tab,
  page = 1,
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  searchQuery = "",
  sort = null,
}: {
  tab: PlaceLibraryTab;
  page?: number;
  pageSize?: LibraryPageSize;
  searchQuery?: string;
  sort?: PlaceLibrarySortState;
}): string {
  const params = new URLSearchParams({
    status: tab,
    page: String(page),
  });

  if (searchQuery) {
    params.set("q", searchQuery);
  }

  appendLibrarySortParams(params, sort);
  appendLibraryPageSizeParam(params, pageSize);

  return `/places/library?${params.toString()}`;
}

export function buildPlaceLibrarySortHref({
  tab,
  searchQuery = "",
  pageSize = DEFAULT_LIBRARY_PAGE_SIZE,
  sort,
  nextColumn,
}: {
  tab: PlaceLibraryTab;
  searchQuery?: string;
  pageSize?: LibraryPageSize;
  sort: PlaceLibrarySortState;
  nextColumn: PlaceLibrarySortColumn;
}): string {
  return buildPlaceLibraryHref({
    tab,
    page: 1,
    pageSize,
    searchQuery,
    sort: getNextLibrarySort(sort, nextColumn),
  });
}

export type PlaceEditorRow = Pick<
  Tables<"places">,
  | "id"
  | "name"
  | "address"
  | "city"
  | "zip_code"
  | "department_name"
  | "website_url"
  | "ticketing_url"
  | "google_place_id"
  | "latitude"
  | "longitude"
  | "place_type"
  | "description_short"
  | "description_long"
  | "cover_image_url"
  | "status"
  | "updated_at"
  | "opening_hours_structured"
  | "internal_notes"
>;

export type PlaceEditor = Omit<PlaceEditorRow, "opening_hours_structured"> & {
  opening_hours_structured: PlaceOpeningHours | null;
};

export type PlaceLinkedEvent = Pick<
  Tables<"events">,
  "id" | "title" | "start_date" | "end_date" | "status" | "event_category"
>;

export const PLACE_LINKED_EVENT_CATEGORY_FILTERS = [
  "ALL",
  "PERMANENT_COLLECTION",
  "TEMPORARY_COLLECTION",
  "ACTIVITY",
] as const;

export type PlaceLinkedEventCategoryFilter =
  (typeof PLACE_LINKED_EVENT_CATEGORY_FILTERS)[number];

export type PlaceLinkedEventCategoryCounts = Record<
  PlaceLinkedEventCategoryFilter,
  number
>;

export function countPlaceLinkedEventsByCategory(
  events: PlaceLinkedEvent[],
): PlaceLinkedEventCategoryCounts {
  const counts: PlaceLinkedEventCategoryCounts = {
    ALL: events.length,
    PERMANENT_COLLECTION: 0,
    TEMPORARY_COLLECTION: 0,
    ACTIVITY: 0,
  };

  for (const event of events) {
    if (event.event_category === "PERMANENT_COLLECTION") {
      counts.PERMANENT_COLLECTION += 1;
    } else if (event.event_category === "TEMPORARY_COLLECTION") {
      counts.TEMPORARY_COLLECTION += 1;
    } else if (event.event_category === "ACTIVITY") {
      counts.ACTIVITY += 1;
    }
  }

  return counts;
}

export function filterPlaceLinkedEventsByCategory(
  events: PlaceLinkedEvent[],
  filter: PlaceLinkedEventCategoryFilter,
): PlaceLinkedEvent[] {
  if (filter === "ALL") return events;
  return events.filter((event) => event.event_category === filter);
}

export type UpdatePlaceContentInput = Pick<
  PlaceEditor,
  | "name"
  | "address"
  | "city"
  | "zip_code"
  | "department_name"
  | "website_url"
  | "ticketing_url"
  | "google_place_id"
  | "latitude"
  | "longitude"
  | "place_type"
  | "description_short"
  | "description_long"
>;

export type UpdatePlaceStatusInput = {
  status: PlaceEditStatusValue;
};

export type UpdatePlaceInternalNotesInput = {
  internalNotes: string | null;
};

export type UpdatePlaceCoverImageInput = {
  coverImageUrl: string | null;
};

export type UpdatePlaceContentRepositoryResult =
  | { ok: true; data: PlaceEditor }
  | { ok: false; error: "duplicate_google_place_id" | "unknown" };

export type UpdatePlaceContentResult =
  | {
      success: true;
      data: PlaceEditor;
      syncedLinkedEventsCount: number;
      googlePlaceIdChanged: boolean;
    }
  | {
      success: false;
      error: "duplicate_google_place_id" | "unknown";
    };

export type UpdatePlaceState =
  | {
      success: true;
      data: PlaceEditor;
      syncedLinkedEventsCount?: number;
      googlePlaceIdChanged?: boolean;
    }
  | { success: false; error?: string; fieldErrors?: Record<string, string> };

export type UploadPlaceCoverImageResult =
  | { success: true; data: PlaceEditor }
  | { success: false; error: string };

export const PLACE_CREATE_DEFAULT_NAME = "Nouveau lieu";

export type PlaceEditorialChecklistState = {
  hasCoverImage: boolean;
  hasName: boolean;
  hasGooglePlace: boolean;
};

export type PlaceEditorialChecklistFields = Pick<
  PlaceEditor,
  | "cover_image_url"
  | "name"
  | "google_place_id"
  | "address"
  | "city"
  | "zip_code"
>;

function hasCompleteGooglePlaceData(
  place: Pick<PlaceEditor, "google_place_id" | "address" | "city" | "zip_code">,
): boolean {
  return (
    Boolean(place.google_place_id?.trim()) &&
    Boolean(place.address?.trim()) &&
    Boolean(place.city?.trim()) &&
    Boolean(place.zip_code?.trim())
  );
}

export function getPlaceEditorialChecklist(
  place: PlaceEditorialChecklistFields,
): PlaceEditorialChecklistState {
  const name = place.name?.trim() ?? "";
  const hasName = Boolean(name) && name !== PLACE_CREATE_DEFAULT_NAME;

  return {
    hasCoverImage: Boolean(place.cover_image_url?.trim()),
    hasName,
    hasGooglePlace: hasCompleteGooglePlaceData(place),
  };
}

export function isPlaceEditorialChecklistComplete(
  place: PlaceEditorialChecklistFields,
): boolean {
  const checklist = getPlaceEditorialChecklist(place);
  return (
    checklist.hasCoverImage && checklist.hasName && checklist.hasGooglePlace
  );
}

export function getPlaceEditorialChecklistProgress(
  place: PlaceEditorialChecklistFields,
): EditorialChecklistProgress {
  const checklist = getPlaceEditorialChecklist(place);
  return countEditorialChecklistProgress([
    checklist.hasCoverImage,
    checklist.hasName,
    checklist.hasGooglePlace,
  ]);
}

export type CreatePlaceInput = {
  name?: string;
  status?: CreateContentInitialStatus;
};

export type CreatePlaceResult =
  | { success: true; data: PlaceEditor }
  | { success: false; error: string };

export type PlaceSelectOption = {
  id: string;
  name: string;
  city: string | null;
};

export const PLACE_SELECT_SEARCH_LIMIT = 20;

export type PlaceSelectSearchResult =
  | { success: true; places: PlaceSelectOption[] }
  | { success: false; error: string };

export type PlaceTaxonomy = {
  artFormIds: string[];
  artisticPeriodIds: string[];
  culturalAreaIds: string[];
  exhibitionTypeIds: string[];
};

export type UpdatePlaceTaxonomyInput = PlaceTaxonomy;

export type UpdatePlaceTaxonomyResult =
  | { success: true }
  | { success: false; error: string };

export type SyncPlaceOpeningHoursResult =
  | { success: true; data: PlaceEditor }
  | { success: false; error: string };

export type SavePlaceOpeningHoursResult =
  | { success: true; data: PlaceEditor }
  | { success: false; error: string };
